// HubSpot tracking helpers.
//
// The HubSpot tracking script itself is loaded by Google Tag Manager, which also
// decides which HubSpot account/portal id to use per environment. These helpers
// just push commands onto HubSpot's `_hsq` queue (which the GTM-loaded script drains):
//   • identify authenticated users by email — done in the app because only the app
//     knows the signed-in user (mirrors the ASP.NET `if (User.Identity.IsAuthenticated)`)
//   • record a page view on each client-side navigation — the SPA equivalent of a
//     full page reload, replacing the GTM "Hubspot Track Page View" History-Change tag

// Always-defined command queue, drained by the HubSpot script (loaded via GTM) once
// it is ready. Pushing before the script loads is supported/expected by HubSpot.
const getQueue = () => {
  if (typeof window === 'undefined') return null
  window._hsq = window._hsq || []
  return window._hsq
}

// Last path we sent a page view for, so back-to-back duplicates collapse to one.
// This guards the race where identifyHubSpot flushes a page view for the same page
// that onRouteUpdate (or a previous identify) just tracked.
let lastTrackedPath = null

const currentPath = () =>
  typeof window !== 'undefined'
    ? `${window.location.pathname}${window.location.search || ''}`
    : ''

// Push a page view, skipping it if it would duplicate the immediately-preceding one
// for the same path.
const sendPageView = (path) => {
  const _hsq = getQueue()
  if (!_hsq) return
  const p = path || currentPath()
  if (p && p === lastTrackedPath) return
  lastTrackedPath = p
  if (p) _hsq.push(['setPath', p])
  _hsq.push(['trackPageView'])
}

// Associate the current visitor with their email (HubSpot merges/dedupes contacts).
//
// `identify` only stashes the email — HubSpot attaches it to the *next* tracked
// page view. We therefore flush a page view here so the identification actually
// reaches HubSpot; this is required so a freshly signed-up visitor's landing page
// (a full page load where auth resolves after the loader's anonymous auto-view, and
// onRouteUpdate skips the initial load) is attributed to their new contact.
//
// sendPageView dedupes against the path onRouteUpdate may have just tracked, so an
// identify that resolves right after a SPA navigation does not double-count it.
export const identifyHubSpot = (email) => {
  const _hsq = getQueue()
  if (!_hsq || !email) return
  _hsq.push(['identify', { email }])
  sendPageView()
}

// Record a virtual page view for client-side (SPA) navigation.
export const trackHubSpotPageView = (path) => {
  sendPageView(path)
}
